/**
 * Inbox — 2-column messaging screen wired to the real `/api/v1/inbox/*`
 * backend (Phase 2 Task 2.1, commit 3e81a7d).
 *
 * Phase 3 Task 3.2 (2026-05-17). Replaces the hardcoded persona fixture
 * THREADS const (Pieter / Anna / Naledi / Reshma / "iProtect Trust
 * Specialists") that the 2026-05-16 audit flagged as a P0 customer-
 * visible leak — every signed-in user saw Anna's inbox.
 *
 * Trust-scope: the inbox surface itself is per-trust (the backend list
 * endpoint requires `trustId`). For multi-trust users we show a picker;
 * for single-trust users we auto-select. For zero-trust users we show
 * the same empty state as TrustScopedLanding.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { displayPersonName } from '../../lib/format/person-name';
import { displayLegalName } from '../../lib/format/legal-name';
import { ScreenShell } from './screen-shell';
import { Icon } from '../icon';
import { useTrusts, useMe } from '../../lib/api';
import { useTrustBeneficiaries } from '../../lib/api/trusts';
import { mapError } from '../../lib/error-messages';
import { useToastStore } from '../../lib/toast-store';
import {
  useInboxThreads,
  useInboxThread,
  useInboxMessages,
  useCreateThread,
  usePostMessage,
  useMarkThreadRead,
  useRedactMessage,
  useArchiveThread,
  useAddThreadParticipant,
  type InboxThreadSummary,
  type InboxThreadStatus,
  type InboxThreadParticipant,
  type InboxMessage,
  type CreateInboxThreadParticipantInput,
} from '../../lib/api/inbox';
// Lane B · B7: trustee roster → add-participant candidates (role 'trustee').
import { useTrustTrustees } from '../../lib/api/trusts';
// ── COHESION PASS, SCREEN 2a (2026-09-15) ───────────────────────────────────
// The thread list was the last hand-rolled row anatomy in the portal, and the
// four states on this screen were a skeleton, a card, a centred block and a
// centred block, all local to this file. See the block above `Inbox` below.
import { EntityRow } from '../entity-row';
import { StateCard } from '../state-card';
import { initialsOf } from '../../lib/format/initials';

// REQ-INBOX-001 Item 9 (POPIA) — the reachable subset of the backend's
// INBOX_REDACT_ROLES ('admin' | 'compliance' | 'io' | 'super_admin'). `io` is
// a POPIA `popiaRole` designation (see admin-io-designate.ts), never stamped
// onto the top-level `app_metadata.role` claim `useMe()` reads here, so it
// cannot appear in this FE role union — the backend gate is still the source
// of truth (a 403 renders an honest error rather than the button lying about
// access). Mirrors the same pre-existing pattern in legal-holds-screen.tsx.
const CAN_REDACT_ROLES = new Set(['admin', 'compliance', 'super_admin']);

// ── Thread filters (COMMS-01) ─────────────────────────────────────────────────

type ThreadFilter = 'all' | 'unread' | 'firm' | 'system' | 'archived';

const FILTERS: { id: ThreadFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'firm', label: 'From firm' },
  { id: 'system', label: 'System' },
  // Lane B · B6: archived threads leave the working list and live here.
  { id: 'archived', label: 'Archived' },
];

// Lane B · B7: roles that may ADD a participant to a thread — mirrors the
// backend `isTenantWideWriter` gate in inbox-thread-participant-add.ts.
// Distinct from CAN_REDACT_ROLES (different backend gate — includes director).
const CAN_MANAGE_PARTICIPANTS = new Set(['admin', 'compliance', 'director', 'super_admin']);

/** Heuristic classifier for the "From firm" / "System" pills. The inbox
 *  summary carries no explicit channel, so we derive one honestly from the
 *  subject: system notices are the automated ones the platform emits
 *  (valuation / FICA / renewal / deadline reminders). Everything else with a
 *  human participant is "from firm". The pills NARROW the already-loaded
 *  list — they never fabricate threads. */
function isSystemThread(t: InboxThreadSummary): boolean {
  return /\b(system|automated|valuation|revaluation|fica|renewal|reminder|deadline|notice)\b/i.test(
    t.subject,
  );
}

// ── Formatters ───────────────────────────────────────────────────────────────

const DATE_FMT = new Intl.DateTimeFormat('en-ZA', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function fmtListTime(iso: string): string {
  try {
    return DATE_FMT.format(new Date(iso));
  } catch {
    return iso;
  }
}

/**
 * MSG-01 (M3 A9) — "user 2B7". The READ side.
 *
 * Paul opened a thread and read a raw identifier where a person's name belongs.
 * The generator was `user-${claims.sub.slice(0, 8)}`, and it was killed at the
 * write side on 26 August. That fix cannot reach the threads Paul was looking
 * at: `authorDisplayName` is STAMPED ONTO THE MESSAGE at post time and the
 * participant's name is stamped onto the participant row, so every string
 * written before the fix is still in Cosmos and still renders. The handler's own
 * header says so: "the name is PERSISTED on the participant row, so it outlived
 * the request."
 *
 * A write-time fix for persisted bad data is not a fix for the data already
 * written. Two ways to finish it, and they are not alternatives:
 *
 *   1. THIS. Refuse to PRINT an identifier. One predicate, applied at every
 *      place this screen renders a stored name, so a row written last month
 *      stops showing its id the next time anyone opens it. It is immediate, it
 *      needs no migration, and it degrades to the same word the backend now
 *      falls back to, so the two agree.
 *   2. A BACKFILL that rewrites the stored strings. That is a data migration
 *      over the inbox containers and it is Paul's call, not an agent's. It is
 *      also the only thing that repairs the data itself: this predicate governs
 *      what THIS screen prints and nothing else, so an export, a notification
 *      email, or the mobile inbox still reads whatever is stored.
 *
 * The match is deliberately narrow. Hiding a real name would be a worse bug than
 * showing an id, so only shapes no South African name can take are caught: the
 * exact `user-<hex>` the old generator produced, a bare UUID, and a lone
 * hex-only token long enough to be a key.
 */
const ID_SHAPED_NAME = [
  /^user[-_\s]?[0-9a-f]{4,}$/i,
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  /^[0-9a-f]{12,}$/i,
];

/** The word to print when the stored string is an identifier, not a name.
 *  Same fallback `inbox-message-post.ts` now writes, so a repaired row and an
 *  unrepaired one read identically. */
const UNNAMED_PARTICIPANT = 'A colleague';

/** A stored display name, or an honest stand-in when what was stored is an id. */
function displayNameOf(stored: string | null | undefined): string {
  const name = (stored ?? '').trim();
  if (!name) return UNNAMED_PARTICIPANT;
  if (ID_SHAPED_NAME.some((re) => re.test(name))) return UNNAMED_PARTICIPANT;
  // The one person-name rule (round 4): an ID-read name renders in title case.
  return displayPersonName(name);
}

/** Human label for an inbox participant's role (the conversation roster). */
function participantRoleLabel(role: InboxThreadParticipant['role']): string {
  switch (role) {
    case 'trustee': return 'Trustee';
    case 'beneficiary': return 'Beneficiary';
    case 'admin': return 'Firm';
    case 'external-counsel': return 'Counsel';
    default: return 'Participant';
  }
}

/** Compact byte size for an attachment chip. */
function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Calendar-day label (SAST) for the message date separators. */
const DAY_FMT = new Intl.DateTimeFormat('en-ZA', {
  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Africa/Johannesburg',
});
function dayLabel(iso: string): string {
  try { return DAY_FMT.format(new Date(iso)); } catch { return iso; }
}

/**
 * Human eyebrow label for the thread-detail header — the OTHER participant(s)
 * on this thread, e.g. "Conversation with Anna Smith, Pieter van Zyl".
 *
 * Unlike the mobile Inbox (which had to batch-resolve names via GET /v1/users
 * because its threads-list wire only carries participantUserIds), the web
 * inbox v2 thread-detail response already returns each participant's real
 * `displayName` (see InboxThreadParticipant) — so no extra network call is
 * needed here, just excluding the caller's own name from the label. Falls
 * back to the neutral "Conversation" when there is no OTHER participant yet
 * (e.g. a solo thread) or the caller's id isn't known yet.
 */
function conversationEyebrow(
  participants: InboxThreadParticipant[],
  currentUserId: string | undefined,
): string {
  const others = participants.filter((p) => p.userId !== currentUserId);
  if (others.length === 0) return 'Conversation';
  return `Conversation with ${others.map((p) => displayNameOf(p.displayName)).join(', ')}`;
}

/** A participant has seen the whole thread iff their lastReadAt is at/after the
 *  thread's last message. Honest read-state, derived from participants[].lastReadAt. */
function hasReadThrough(p: InboxThreadParticipant, lastMessageAt: string): boolean {
  if (!p.lastReadAt) return false;
  const read = new Date(p.lastReadAt).getTime();
  const last = new Date(lastMessageAt).getTime();
  if (Number.isNaN(read) || Number.isNaN(last)) return false;
  return read >= last;
}

// ── Visual primitives ────────────────────────────────────────────────────────

interface AvatarProps {
  initials: string;
  color?: string;
  size?: number;
}

function Avatar({ initials, color = '', size = 36 }: AvatarProps) {
  return (
    <div className={'avatar ' + color} style={{ width: size, height: size, fontSize: size <= 30 ? 11 : 13 }}>
      {initials}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div
      data-testid="inbox-detail-skeleton"
      aria-busy="true"
      aria-live="polite"
      style={{ padding: '32px 36px' }}
    >
      <div style={{ height: 12, width: '30%', background: 'var(--surface-2)', borderRadius: 4 }} />
      <div style={{ height: 24, width: '70%', background: 'var(--surface-2)', borderRadius: 4, marginTop: 12 }} />
      <div style={{ height: 14, width: '40%', background: 'var(--surface-2)', borderRadius: 4, marginTop: 12 }} />
      <div style={{ height: 100, width: '100%', background: 'var(--surface-2)', borderRadius: 4, marginTop: 24 }} />
    </div>
  );
}

// ── Inbox screen ─────────────────────────────────────────────────────────────

export function Inbox() {
  const trustsQ = useTrusts();
  const me = useMe().data?.user;
  const trustsData = trustsQ.data?.trusts;
  const trusts = useMemo(() => trustsData ?? [], [trustsData]);

  // Trust selection — auto-pick the first trust; let the user choose when
  // there are multiple. (Most beneficiary users have one; admin RMs span many.)
  const [activeTrustId, setActiveTrustId] = useState<string | null>(null);
  useEffect(() => {
    if (activeTrustId) return;
    const first = trusts[0];
    if (first) {
      setActiveTrustId(first.id);
    }
  }, [trusts, activeTrustId]);

  const threadsQ = useInboxThreads({ trustId: activeTrustId ?? undefined });
  const threadsData = threadsQ.data?.threads;
  const allThreads: InboxThreadSummary[] = useMemo(
    () => threadsData ?? [],
    [threadsData],
  );

  // Active filter pill (COMMS-01) — narrows the loaded thread list only.
  const [filter, setFilter] = useState<ThreadFilter>('all');
  // Lane B · B6: the four working filters show OPEN threads only — keeping
  // archived rows in the working list forever is what archiving exists to
  // stop; the Archived pill is the one place they surface.
  const threads = useMemo(() => {
    const open = allThreads.filter((t) => t.status !== 'archived');
    switch (filter) {
      case 'unread':
        return open.filter((t) => t.unreadForYou);
      case 'system':
        return open.filter((t) => isSystemThread(t));
      case 'firm':
        return open.filter((t) => !isSystemThread(t));
      case 'archived':
        return allThreads.filter((t) => t.status === 'archived');
      case 'all':
      default:
        return open;
    }
  }, [allThreads, filter]);

  /* WHAT IS BEHIND EACH CHIP. Counted off the SAME predicates the filter
     itself uses, in one place, so a chip cannot claim a number the filter then
     fails to produce. Read off `allThreads` rather than the visible set,
     because a count that changes when you select it is not a count. */
  const filterCounts = useMemo<Record<ThreadFilter, number>>(() => {
    const open = allThreads.filter((t) => t.status !== 'archived');
    return {
      all: open.length,
      unread: open.filter((t) => t.unreadForYou).length,
      firm: open.filter((t) => !isSystemThread(t)).length,
      system: open.filter((t) => isSystemThread(t)).length,
      archived: allThreads.filter((t) => t.status === 'archived').length,
    };
  }, [allThreads]);

  // Recipient candidates for the compose modal — real named beneficiaries of
  // the active trust who are also app users (have a userId). The caller is
  // auto-added server-side, so they need not appear here.
  const beneficiariesQ = useTrustBeneficiaries({ trustId: activeTrustId ?? undefined });
  const recipientCandidates: CreateInboxThreadParticipantInput[] = useMemo(() => {
    const rows = beneficiariesQ.data?.beneficiaries ?? [];
    return rows
      .filter((b) => b.kind === 'named' && !!b.userId)
      .map((b) => ({
        userId: b.userId as string,
        displayName: b.displayName,
        role: 'beneficiary' as const,
      }));
  }, [beneficiariesQ.data]);

  // Compose modal state.
  const [composeOpen, setComposeOpen] = useState(false);
  const createThread = useCreateThread();

  // Active thread — default to the first thread once threads arrive.
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  // QA finding: compose "jump straight into the freshly-created thread"
  // (below) sets `activeThreadId` to the new id right after `useCreateThread`
  // fires a fire-and-forget `invalidateQueries(['inbox'])` — the threads-list
  // cache is still the OLD (stale) list at that instant. That re-runs THIS
  // effect with an `activeThreadId` the (stale) `threads` array doesn't
  // contain yet, which used to reset it straight back to the previous
  // first thread. Track the pending id here so the effect trusts a
  // deliberate jump until the list catches up, instead of clobbering it.
  const pendingThreadIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (threads.length === 0) {
      setActiveThreadId(null);
      return;
    }
    const first = threads[0];
    if (!first) return;
    if (activeThreadId && activeThreadId === pendingThreadIdRef.current) {
      if (threads.some((t) => t.id === activeThreadId)) {
        pendingThreadIdRef.current = null; // caught up — resume normal defaulting
      }
      return;
    }
    if (!activeThreadId || !threads.some((t) => t.id === activeThreadId)) {
      setActiveThreadId(first.id);
    }
  }, [threads, activeThreadId]);

  const threadQ = useInboxThread(activeThreadId ?? undefined, activeTrustId ?? undefined);
  const messagesQ = useInboxMessages(activeThreadId ?? undefined, activeTrustId ?? undefined);
  const messages: InboxMessage[] = messagesQ.data?.messages ?? [];

  // Mark-read side effect — fire once when the active thread loads + is unread.
  const markRead = useMarkThreadRead();
  useEffect(() => {
    if (!activeThreadId || !activeTrustId) return;
    if (!threadQ.data?.unreadForYou) return;
    void markRead.mutateAsync({ threadId: activeThreadId, trustId: activeTrustId }).catch(() => {
      // Failure here is non-blocking — leave the indicator alone.
    });
    // We DELIBERATELY exclude `markRead` from deps — its identity changes
    // every render and would re-fire the mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThreadId, activeTrustId, threadQ.data?.unreadForYou]);

  // Composer.
  const postMessage = usePostMessage();
  const [draft, setDraft] = useState('');
  const canSend = draft.trim().length > 0 && !!activeThreadId && !!activeTrustId && !postMessage.isPending;

  async function onSendClick() {
    if (!canSend) return;
    const body = draft.trim();
    try {
      await postMessage.mutateAsync({
        threadId: activeThreadId!,
        trustId: activeTrustId!,
        body,
      });
      setDraft('');
    } catch {
      // Errors surface via postMessage.error in the UI; nothing else to do.
    }
  }

  // Redact (REQ-INBOX-001 Item 9, POPIA) — confirm-before-mutate since
  // redaction is irreversible from the product UI. `redactTarget` is the
  // message the confirm dialog is currently open for, or null.
  const pushToast = useToastStore((s) => s.pushToast);
  const canRedact = !!me?.role && CAN_REDACT_ROLES.has(me.role);
  const redactMessage = useRedactMessage();
  const [redactTarget, setRedactTarget] = useState<InboxMessage | null>(null);

  // Lane B · B6: archive the active thread (participant-gated server-side; no
  // un-archive endpoint exists, so a confirm dialog gates the click).
  const archiveThread = useArchiveThread();
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  function handleArchiveConfirm() {
    if (!activeThreadId || !activeTrustId) return;
    archiveThread.mutate(
      { threadId: activeThreadId, trustId: activeTrustId },
      {
        onSuccess: () => {
          pushToast({ kind: 'success', message: 'Thread archived. Find it under the Archived filter.' });
          setArchiveConfirmOpen(false);
        },
        onError: (err) => {
          pushToast({ kind: 'error', message: `Couldn't archive this thread: ${mapError(err, 'save')}` });
          setArchiveConfirmOpen(false);
        },
      },
    );
  }

  // Lane B · B7: add a participant (admin-tier only — mirrors the backend
  // isTenantWideWriter gate). Candidates: named beneficiaries with a portal
  // account + active trustees with one, minus existing participants.
  const canManageParticipants = !!me?.role && CAN_MANAGE_PARTICIPANTS.has(me.role);
  const addParticipant = useAddThreadParticipant();
  const trusteesQ = useTrustTrustees(canManageParticipants ? (activeTrustId ?? undefined) : undefined);
  const participantCandidates: CreateInboxThreadParticipantInput[] = useMemo(() => {
    const trusteeRows = (trusteesQ.data?.trustees ?? [])
      .filter((t) => t.status === 'active' && !!t.userId)
      .map((t) => ({
        userId: t.userId as string,
        displayName: t.displayName,
        role: 'trustee' as const,
      }));
    // Beneficiary candidates reuse the compose list; dedupe on userId with
    // trustees taking precedence (a trustee who is also a beneficiary joins
    // as trustee).
    const seen = new Set(trusteeRows.map((t) => t.userId));
    return [...trusteeRows, ...recipientCandidates.filter((c) => !seen.has(c.userId))];
  }, [trusteesQ.data, recipientCandidates]);
  function handleAddParticipant(candidate: CreateInboxThreadParticipantInput) {
    if (!activeThreadId || !activeTrustId) return;
    addParticipant.mutate(
      { threadId: activeThreadId, trustId: activeTrustId, ...candidate },
      {
        onSuccess: () => {
          pushToast({ kind: 'success', message: `${displayPersonName(candidate.displayName)} added. They can read the full thread history.` });
        },
        onError: (err) => {
          pushToast({ kind: 'error', message: `Couldn't add them: ${mapError(err, 'save')}` });
        },
      },
    );
  }

  function handleRedactConfirm() {
    if (!redactTarget || !activeTrustId) return;
    redactMessage.mutate(
      { threadId: redactTarget.threadId, messageId: redactTarget.id, trustId: activeTrustId },
      {
        onSuccess: () => {
          pushToast({ kind: 'success', message: 'Message redacted.' });
          setRedactTarget(null);
        },
        onError: (err) => {
          if ((err as { isStepUpRequired?: boolean }).isStepUpRequired) return; // global interceptor handles re-auth + replay
          pushToast({ kind: 'error', message: `Couldn't redact this message: ${mapError(err, 'save')}` });
        },
      },
    );
  }

  // Sub-headline string for the topbar — counts off the FULL list (not the
  // active filter) so the header reflects the trust, not the current view.
  const unreadCount = allThreads.filter((t) => t.unreadForYou).length;
  const subLine = threadsQ.isLoading
    ? 'Loading messages for this trust…'
    : threadsQ.isError
    ? 'Couldn’t load messages. Try again in a moment.'
    : allThreads.length === 0
    ? 'No messages yet on this trust.'
    : unreadCount === 0
    ? `${allThreads.length} ${allThreads.length === 1 ? 'thread' : 'threads'} · nothing unread`
    : `${unreadCount} unread of ${allThreads.length} ${allThreads.length === 1 ? 'thread' : 'threads'}`;

  return (
    <ScreenShell
      activeNav="inbox"
      /* ONE HEADER LINE, AND ONE PIECE OF LARGE TYPE (cohesion pass, 2026-09-15).
         The screen used to carry `Inbox.` as a 34px italic-serif h1 with a full
         stop, then a trust picker on its own labelled row, then a right-aligned
         New message button on a third row, and only then the content: three
         bands of chrome above a master/detail pair that needs the height. The
         eyebrow now says where you are, the h1 names what is under it, and the
         two controls sit in the shell's own topbar slot beside them. */
      eyebrow="Inbox"
      title="Conversations"
      sub={subLine}
      topbarRight={
        <div className="topbar-actions">
          {/* Trust picker — only when the user has more than one trust. */}
          {trusts.length > 1 && (
            <select
              id="inbox-trust-picker"
              aria-label="Trust"
              value={activeTrustId ?? ''}
              onChange={(e) => {
                setActiveTrustId(e.target.value);
                setActiveThreadId(null);
              }}
              style={{
                padding: '7px 12px',
                borderRadius: 'var(--r-control)',
                border: '1px solid var(--hairline-2)',
                background: 'var(--surface)',
                color: 'var(--ink)',
                fontFamily: 'var(--sans)',
                fontSize: 13,
                maxWidth: 220,
              }}
            >
              {trusts.map((t) => (
                <option key={t.id} value={t.id}>
                  {displayLegalName(t.name)}
                </option>
              ))}
            </select>
          )}
          {/* New-message action — opens the compose modal. Disabled until a
              trust is selected (the backend requires a trustId). */}
          <button
            type="button"
            className="btn-primary"
            onClick={() => setComposeOpen(true)}
            disabled={!activeTrustId}
            title={activeTrustId ? undefined : 'Choose a trust first.'}
          >
            <Icon name="msg" size={14} stroke={1.6} />
            New message
          </button>
        </div>
      }
    >
      {/* 348px for the thread column, per the contract: wide enough for a
          sender, a subject and a one-line preview, and no wider, because the
          conversation is what the reader came for. */}
      <div style={{ display: 'grid', gridTemplateColumns: '348px minmax(0, 1fr)', gap: 'var(--card-gap)', minHeight: 720 }}>
        {/* ── Thread list ─────────────────────────────────────────────────── */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {/* ONE FILTER ROW, WITH COUNTS. Five chips that said nothing about
              what was behind them: a reader could not tell whether `Unread`
              held one thread or none without clicking it, and clicking an
              empty filter is how a working inbox reads as a broken one. A
              zero-count chip stays visible and goes DISABLED rather than
              disappearing, because a filter row that changes shape as you use
              it is disorienting (contract, "Filters and segmented controls").
              The role="tab" markup is unchanged: it was already right, and the
              existing suite walks it. */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--hairline)', display: 'flex', gap: 6, flexWrap: 'wrap' }} role="tablist" aria-label="Filter threads">
            {FILTERS.map((f) => {
              const active = filter === f.id;
              const count = filterCounts[f.id];
              return (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  disabled={count === 0 && !active}
                  onClick={() => setFilter(f.id)}
                  style={{
                    background: active ? 'var(--surface-2)' : 'transparent',
                    border: 'none',
                    padding: '5px 11px',
                    borderRadius: 999,
                    fontSize: 12.5,
                    fontFamily: 'var(--sans)',
                    color: active ? 'var(--ink)' : count === 0 ? 'var(--ink-4)' : 'var(--ink-2)',
                    cursor: count === 0 && !active ? 'default' : 'pointer',
                    fontWeight: active ? 600 : 400,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  {f.label}
                  <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--ink-4)' }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* FOUR STATES, ONE SHAPE. These were a bespoke four-bar skeleton, a
              card with an italic-serif heading, a 48px centred block with a
              32px glyph, and a 40px centred block with an underlined link:
              four anatomies for four states of one list. The state card cannot
              show a number while it is loading and cannot omit the "nothing
              was changed" line on an error, which is the whole reason the
              rules live in a component rather than in a habit. */}
          {threadsQ.isLoading || threadsQ.isError || threads.length === 0 ? (
            <div style={{ padding: 20 }}>
              {threadsQ.isLoading ? (
                <StateCard
                  variant="loading"
                  testId="inbox-threads"
                  label="Conversations"
                  headline="Reading the correspondence on this trust."
                />
              ) : threadsQ.isError ? (
                <StateCard
                  variant="error"
                  testId="inbox-threads"
                  label="Conversations"
                  headline="The messages on this trust could not be read just now."
                  detail={mapError(threadsQ.error, 'load')}
                  action={{ label: 'Try again', onClick: () => void threadsQ.refetch() }}
                />
              ) : allThreads.length === 0 ? (
                /* QA finding (2026-07-16): a firm-managed client member with
                   zero trust memberships reaches this empty state with
                   `activeTrustId === null`, and the compose modal only mounts
                   when `composeOpen && activeTrustId`, so the action used to
                   produce ZERO visible feedback. The action is now simply
                   absent for them, and the copy says why. */
                <StateCard
                  variant="empty"
                  testId="inbox-threads"
                  label="Conversations"
                  headline={
                    activeTrustId
                      ? 'No messages on this trust yet'
                      : 'You are not yet assigned to a trust'
                  }
                  detail={
                    activeTrustId
                      ? 'Start a thread with a trustee, beneficiary or co-administrator. Every message is recorded in the trust audit log alongside the documents it references.'
                      : 'Once you are, this is where you can message a trustee, beneficiary or co-administrator.'
                  }
                  {...(activeTrustId
                    ? { action: { label: 'Start a thread', onClick: () => setComposeOpen(true) } }
                    : {})}
                />
              ) : (
                /* The honest fallback, and it should now be UNREACHABLE: each
                   chip is counted off the same predicate its filter uses, so a
                   chip reading 0 is disabled and a reader cannot select a
                   filter with nothing behind it. Kept for the day those two
                   diverge, because an empty list saying nothing is worse than
                   one line of dead markup. */
                <StateCard
                  variant="empty"
                  testId="inbox-filter"
                  label="Conversations"
                  headline="No threads match this filter"
                  detail="The rest of this trust's correspondence is still here."
                  action={{ label: 'Show all threads', onClick: () => setFilter('all') }}
                />
              )}
            </div>
          ) : null}

          {/* THE SHARED ROW, ON THE LAST HAND-ROLLED LIST IN THE PORTAL.
              This was a 40px-plus-1fr grid with a THREE-pixel left border that
              carried two unrelated facts on one edge (brand green for
              selected, brass for unread, transparent otherwise), an uppercase
              `SYSTEM` / `FIRM` label, and its own hover and border rules. It
              is now `EntityRow`: the icon roundel in the leading track, the
              subject in the subject track, one chip carrying the time, and the
              ONE 2px marker mechanism, which selection owns because "this is
              the one you are reading" is the more useful of the two facts.
              Unread is now the brass chip plus the word, not a second edge.

              The subject takes `subjectKind="text"`: a message subject is not
              a named entity, and italic serif in this product means exactly
              one thing. */}
          {!threadsQ.isLoading && !threadsQ.isError && threads.map((t) => {
            const system = isSystemThread(t);
            const active = activeThreadId === t.id;
            return (
              <EntityRow
                key={t.id}
                testId={`thread-${t.id}`}
                selected={active}
                onClick={() => setActiveThreadId(t.id)}
                subjectKind="text"
                name={t.subject}
                leading={
                  <span
                    aria-hidden="true"
                    style={{
                      width: 34, height: 34, borderRadius: 'var(--r-control)',
                      display: 'grid', placeItems: 'center', flexShrink: 0,
                      background: system ? 'var(--accent-50)' : 'var(--primary-50)',
                      color: system ? 'var(--accent-700)' : 'var(--primary-700)',
                    }}
                  >
                    <Icon name={system ? 'bell' : 'msg'} size={16} stroke={1.7} />
                  </span>
                }
                description={
                  <>
                    {system ? 'System' : 'From the firm'}
                    {' · '}{t.messageCount} {t.messageCount === 1 ? 'message' : 'messages'}
                    {' · '}{t.participantCount} {t.participantCount === 1 ? 'participant' : 'participants'}
                    {t.attachmentsCount > 0
                      ? ` · ${t.attachmentsCount} attachment${t.attachmentsCount === 1 ? '' : 's'}`
                      : ''}
                    {t.status === 'archived' ? ' · Archived' : ''}
                    {t.unreadForYou ? ' · 1 unread' : ''}
                  </>
                }
                urgency={fmtListTime(t.lastMessageAt)}
                tone={t.unreadForYou ? 'soon' : 'routine'}
              />
            );
          })}
        </div>

        {/* ── Thread detail ───────────────────────────────────────────────── */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {!activeThreadId && !threadsQ.isLoading && (
            <div style={{ padding: 24 }}>
              <StateCard
                variant="empty"
                testId="inbox-no-selection"
                label="Conversation"
                headline="Choose a conversation on the left to read it"
                detail="Messages here form part of the trust's permanent record."
              />
            </div>
          )}

          {activeThreadId && (threadQ.isLoading || messagesQ.isLoading) && <DetailSkeleton />}

          {activeThreadId && (threadQ.isError || messagesQ.isError) && (
            <div style={{ padding: 24 }}>
              <StateCard
                variant="error"
                testId="inbox-thread"
                label="Conversation"
                headline="This conversation could not be read just now."
                detail={mapError(threadQ.error ?? messagesQ.error, 'load')}
              />
            </div>
          )}

          {activeThreadId && threadQ.data && messagesQ.data && (
            <ThreadDetailPanel
              subject={threadQ.data.thread.subject}
              status={threadQ.data.thread.status}
              participants={threadQ.data.thread.participants}
              lastMessageAt={threadQ.data.thread.lastMessageAt}
              currentUserId={me?.supabaseUserId}
              messages={messages}
              draft={draft}
              onDraftChange={setDraft}
              canSend={canSend}
              isSending={postMessage.isPending}
              sendError={postMessage.error ? mapError(postMessage.error, 'submit-request') : undefined}
              onSend={() => void onSendClick()}
              canRedact={canRedact}
              onRequestRedact={setRedactTarget}
              archive={
                threadQ.data.youAreParticipant && threadQ.data.thread.status === 'open'
                  ? { onRequestArchive: () => setArchiveConfirmOpen(true), pending: archiveThread.isPending }
                  : undefined
              }
              addParticipant={
                canManageParticipants && threadQ.data.thread.status === 'open'
                  ? {
                      candidates: participantCandidates.filter(
                        (c) => !threadQ.data.thread.participants.some((pp) => pp.userId === c.userId),
                      ),
                      onAdd: handleAddParticipant,
                      pending: addParticipant.isPending,
                    }
                  : undefined
              }
            />
          )}
        </div>
      </div>

      {composeOpen && activeTrustId && (
        <ComposeModal
          trustId={activeTrustId}
          recipientCandidates={recipientCandidates}
          recipientsLoading={beneficiariesQ.isLoading}
          isCreating={createThread.isPending}
          createError={
            createThread.error ? mapError(createThread.error, 'submit-request') : undefined
          }
          onClose={() => setComposeOpen(false)}
          onSubmit={async ({ subject, participants, initialBody }) => {
            const created = await createThread.mutateAsync({
              trustId: activeTrustId,
              subject,
              participants,
              ...(initialBody.trim() ? { initialMessage: { body: initialBody.trim() } } : {}),
            });
            setComposeOpen(false);
            // Jump straight into the freshly-created thread. Mark it PENDING
            // first — the threads-list cache is still stale at this instant
            // (useCreateThread only invalidates, it doesn't optimistically
            // write the new thread in), so the default-selection effect must
            // not clobber this deliberate jump before the refetch lands.
            pendingThreadIdRef.current = created.thread.id;
            setActiveThreadId(created.thread.id);
          }}
        />
      )}

      {redactTarget && (
        <RedactConfirmModal
          isPending={redactMessage.isPending}
          error={
            redactMessage.isError && !(redactMessage.error as { isStepUpRequired?: boolean }).isStepUpRequired
              ? mapError(redactMessage.error, 'save')
              : undefined
          }
          onCancel={() => setRedactTarget(null)}
          onConfirm={handleRedactConfirm}
        />
      )}

      {archiveConfirmOpen && (
        <ArchiveConfirmModal
          isPending={archiveThread.isPending}
          onCancel={() => setArchiveConfirmOpen(false)}
          onConfirm={handleArchiveConfirm}
        />
      )}
    </ScreenShell>
  );
}

// ── Lane B · B6: archive confirm modal ───────────────────────────────────────
//
// There is no un-archive endpoint, so archiving always takes an explicit
// confirm (mirrors the redact-confirm idiom; lighter copy — the thread stays
// readable).
function ArchiveConfirmModal({
  isPending,
  onCancel,
  onConfirm,
}: {
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Archive thread"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20, 18, 15, 0.42)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 24,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isPending) onCancel();
      }}
    >
      <div className="card" style={{ width: 'min(440px, 100%)', padding: 24 }}>
        <h3 className="serif-heading" style={{ fontWeight: 300, fontSize: 22, margin: '0 0 10px' }}>
          Archive this thread?
        </h3>
        <p style={{ margin: '0 0 4px', fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>
          The thread moves out of the working list to the <b>Archived</b> filter
          for every participant. It stays readable, but{' '}
          <b>it cannot be reopened from the app</b>. Start a new thread to
          continue the conversation.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={isPending}>
            Keep it active
          </button>
          <button
            type="button"
            className="btn-primary"
            data-testid="archive-confirm"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? 'Archiving…' : 'Archive thread'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Lane B · B7: add-participant control (thread header roster) ──────────────
//
// Admin-tier only (gated by the caller). Collapsed: an "+ Add person" chip at
// the end of the participant roster. Expanded: a select of trust people with a
// portal account (trustees + named beneficiaries, minus existing
// participants) + explicit history-visibility copy — a new participant sees
// the FULL prior thread history (message reads are thread-level).
function AddParticipantControl({
  candidates,
  onAdd,
  pending,
}: {
  candidates: CreateInboxThreadParticipantInput[];
  onAdd: (candidate: CreateInboxThreadParticipantInput) => void;
  pending: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');

  if (!open) {
    return (
      <button
        type="button"
        data-testid="add-participant-open"
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'transparent', border: '1px dashed var(--hairline-2)',
          borderRadius: 999, padding: '4px 12px', fontSize: 12.5,
          color: 'var(--ink-2)', cursor: 'pointer', fontFamily: 'var(--sans)',
        }}
      >
        + Add person
      </button>
    );
  }

  const chosen = candidates.find((c) => c.userId === selected);

  return (
    <span
      data-testid="add-participant-control"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
    >
      {candidates.length === 0 ? (
        <span data-testid="add-participant-none" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          Everyone on this trust with a portal account is already on the thread.
        </span>
      ) : (
        <>
          <select
            data-testid="add-participant-select"
            aria-label="Choose a person to add to this thread"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            style={{
              fontSize: 12.5, padding: '5px 9px', borderRadius: 8, maxWidth: 220,
              border: '1px solid var(--hairline-2)', background: 'var(--surface)', color: 'var(--ink)',
            }}
          >
            <option value="">Choose a person…</option>
            {candidates.map((c) => (
              <option key={c.userId} value={c.userId}>
                {displayPersonName(c.displayName)} · {participantRoleLabel(c.role)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn-secondary"
            data-testid="add-participant-confirm"
            disabled={!chosen || pending}
            onClick={() => { if (chosen) { onAdd(chosen); setSelected(''); setOpen(false); } }}
            style={{ fontSize: 12, padding: '5px 11px', height: 'auto' }}
          >
            {pending ? 'Adding…' : 'Add'}
          </button>
        </>
      )}
      <button
        type="button"
        aria-label="Cancel adding a person"
        onClick={() => { setOpen(false); setSelected(''); }}
        disabled={pending}
        style={{
          border: 'none', background: 'transparent', padding: 0,
          color: 'var(--ink-3)', fontSize: 12, textDecoration: 'underline', cursor: 'pointer',
        }}
      >
        Cancel
      </button>
      {selected && (
        <span style={{ fontSize: 11.5, color: 'var(--ink-3)', width: '100%' }}>
          They&rsquo;ll see the <b>full thread history</b>, including messages sent before they joined.
        </span>
      )}
    </span>
  );
}

// ── Redact confirm modal (REQ-INBOX-001 Item 9, POPIA) ───────────────────────
//
// Redaction is irreversible from the product UI (the backend records
// `redactedAt`; there is no un-redact endpoint), so this always requires an
// explicit confirm click — never a single-click destructive action.
function RedactConfirmModal({
  isPending,
  error,
  onCancel,
  onConfirm,
}: {
  isPending: boolean;
  error: string | undefined;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Redact message"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20, 18, 15, 0.42)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 24,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isPending) onCancel();
      }}
    >
      <div className="card" style={{ width: 'min(440px, 100%)', padding: 24 }}>
        <h3 className="serif-heading" style={{ fontWeight: 300, fontSize: 22, margin: '0 0 10px' }}>
          Redact this message?
        </h3>
        <p style={{ margin: '0 0 4px', fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>
          The message body will be permanently replaced with{' '}
          <span className="mono" style={{ fontSize: 12.5 }}>[redacted]</span> for every
          participant. <b>This cannot be undone.</b> The message row itself is
          kept for audit-trail and thread-integrity purposes.
        </p>
        {error && (
          <p role="alert" style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--negative)' }}>
            {error}
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            style={{ background: 'var(--negative)', borderColor: 'var(--negative)' }}
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? 'Redacting…' : 'Redact message'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Compose modal (COMMS-01) ──────────────────────────────────────────────────

interface ComposeSubmit {
  subject: string;
  participants: CreateInboxThreadParticipantInput[];
  initialBody: string;
}

interface ComposeModalProps {
  trustId: string;
  recipientCandidates: CreateInboxThreadParticipantInput[];
  recipientsLoading: boolean;
  isCreating: boolean;
  createError: string | undefined;
  onClose: () => void;
  onSubmit: (v: ComposeSubmit) => Promise<void>;
}

function ComposeModal({
  recipientCandidates,
  recipientsLoading,
  isCreating,
  createError,
  onClose,
  onSubmit,
}: ComposeModalProps) {
  const [subject, setSubject] = useState('');
  const [initialBody, setInitialBody] = useState('');
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const selectedParticipants = recipientCandidates.filter((c) => selected[c.userId]);
  // The caller is auto-added server-side; a thread needs at least the caller,
  // so we allow zero explicit recipients only when there are candidates the
  // user simply hasn't picked — but require a subject always.
  const canCreate = subject.trim().length > 0 && !isCreating;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="New message"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20, 18, 15, 0.42)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ width: 'min(560px, 100%)', maxHeight: '88vh', overflowY: 'auto', padding: 28 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eyebrow">New thread</div>
        <h2 className="serif-heading" style={{ fontWeight: 300, fontSize: 26, margin: '6px 0 18px' }}>
          Start a conversation
        </h2>

        {/* MSG-02 (M3 B4) — "when on new message, we need to put here: who do
            you want to send this message to? Otherwise it's just strange."
            The form opened on a Subject field and put the recipient picker
            second, under the bare word "Recipients". A message is addressed
            before it is titled, which is the order every mail client uses and
            the order Paul asked for out loud. So: who, then what about, then
            what. The question is the label, not a heading, so it is what a
            screen reader announces on the checkbox group too. */}
        <div
          id="compose-recipients-label"
          style={{ fontSize: 13.5, color: 'var(--ink-2)', marginBottom: 8 }}
        >
          Who do you want to send this message to?
        </div>
        {recipientsLoading ? (
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 18 }}>
            Loading recipients…
          </div>
        ) : recipientCandidates.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 18, lineHeight: 1.5 }}>
            No beneficiary recipients are registered on this trust yet. The
            thread will be opened with you as the only participant. Add
            beneficiaries to the register to message them here.
          </div>
        ) : (
          <div
            role="group"
            aria-labelledby="compose-recipients-label"
            data-testid="compose-recipients"
            style={{
              border: '1px solid var(--hairline)',
              borderRadius: 10,
              maxHeight: 200,
              overflowY: 'auto',
              marginBottom: 18,
            }}
          >
            {recipientCandidates.map((c) => (
              <label
                key={c.userId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  borderBottom: '1px solid var(--hairline)',
                  cursor: 'pointer',
                  fontSize: 14,
                  color: 'var(--ink)',
                }}
              >
                <input
                  type="checkbox"
                  checked={!!selected[c.userId]}
                  onChange={(e) => setSelected((s) => ({ ...s, [c.userId]: e.target.checked }))}
                />
                <span>{displayPersonName(c.displayName)}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--ink-3)', textTransform: 'capitalize' }}>
                  {c.role}
                </span>
              </label>
            ))}
          </div>
        )}

        <label htmlFor="compose-subject" style={{ display: 'block', fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 6 }}>
          Subject
        </label>
        <input
          id="compose-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={200}
          placeholder="e.g. Quarterly distribution approval"
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid var(--hairline-2)',
            background: 'var(--bg)',
            color: 'var(--ink)',
            fontFamily: 'var(--sans)',
            fontSize: 14,
            boxSizing: 'border-box',
            marginBottom: 18,
          }}
        />

        <label htmlFor="compose-body" style={{ display: 'block', fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 6 }}>
          Message <span style={{ color: 'var(--ink-4, var(--ink-3))' }}>(optional)</span>
        </label>
        <textarea
          id="compose-body"
          value={initialBody}
          onChange={(e) => setInitialBody(e.target.value)}
          rows={4}
          maxLength={10000}
          placeholder="Write the first message. Every message is recorded in the trust audit log."
          style={{
            width: '100%',
            padding: '12px 14px',
            borderRadius: 10,
            border: '1px solid var(--hairline-2)',
            background: 'var(--bg)',
            color: 'var(--ink)',
            fontFamily: 'var(--sans)',
            fontSize: 14,
            lineHeight: 1.5,
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
        />

        {selectedParticipants.length > 0 && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10 }}>
            {selectedParticipants.length}{' '}
            {selectedParticipants.length === 1 ? 'recipient' : 'recipients'} selected.
          </div>
        )}

        {createError && (
          <div role="alert" style={{ color: 'var(--ink-2)', fontSize: 12.5, marginTop: 10 }}>
            Your draft is still here. {createError}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 22 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isCreating}
            style={{
              background: 'transparent',
              color: 'var(--ink-2)',
              border: '1px solid var(--hairline-2)',
              padding: '10px 18px',
              borderRadius: 10,
              fontFamily: 'var(--sans)',
              fontSize: 14,
              cursor: isCreating ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canCreate}
            onClick={() =>
              void onSubmit({ subject: subject.trim(), participants: selectedParticipants, initialBody })
            }
            style={{
              background: canCreate ? 'var(--primary-700)' : 'var(--surface-2)',
              color: canCreate ? 'var(--surface)' : 'var(--ink-3)',
              border: 'none',
              padding: '10px 20px',
              borderRadius: 10,
              fontFamily: 'var(--sans)',
              fontSize: 14,
              fontWeight: 500,
              cursor: canCreate ? 'pointer' : 'not-allowed',
            }}
          >
            {isCreating ? 'Creating…' : 'Create thread'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Empty state ──────────────────────────────────────────────────────────────

// ── Thread detail panel ──────────────────────────────────────────────────────

interface ThreadDetailPanelProps {
  subject: string;
  status: InboxThreadStatus;
  participants: InboxThreadParticipant[];
  lastMessageAt: string;
  currentUserId: string | undefined;
  messages: InboxMessage[];
  draft: string;
  onDraftChange: (v: string) => void;
  canSend: boolean;
  isSending: boolean;
  sendError: string | undefined;
  onSend: () => void;
  /** REQ-INBOX-001 Item 9 — whether the caller's role can redact messages
   *  (mirrors the reachable subset of the backend's INBOX_REDACT_ROLES). */
  canRedact: boolean;
  /** Opens the redact confirm dialog for this message. */
  onRequestRedact: (message: InboxMessage) => void;
  /** Lane B · B6: present when the caller may archive (participant + open). */
  archive?: { onRequestArchive: () => void; pending: boolean };
  /** Lane B · B7: present when the caller may add participants (admin-tier +
   *  open thread). Candidates already exclude existing participants. */
  addParticipant?: {
    candidates: CreateInboxThreadParticipantInput[];
    onAdd: (candidate: CreateInboxThreadParticipantInput) => void;
    pending: boolean;
  };
}

function ThreadDetailPanel({
  subject,
  status,
  participants,
  lastMessageAt,
  currentUserId,
  messages,
  draft,
  onDraftChange,
  canSend,
  isSending,
  sendError,
  onSend,
  canRedact,
  onRequestRedact,
  archive,
  addParticipant,
}: ThreadDetailPanelProps) {
  const everyoneRead =
    participants.length > 0 && participants.every((p) => hasReadThrough(p, lastMessageAt));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header — subject + participant roster (roles + read-state) + governance */}
      <div style={{ padding: '24px 32px 18px', borderBottom: '1px solid var(--hairline)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div className="eyebrow">{conversationEyebrow(participants, currentUserId)}</div>
          {/* Lane B · B6: any participant may archive an open thread. */}
          {archive && (
            <button
              type="button"
              data-testid="thread-archive-open"
              className="btn-secondary"
              onClick={archive.onRequestArchive}
              disabled={archive.pending}
              style={{ fontSize: 12, padding: '5px 12px', height: 'auto', flexShrink: 0 }}
            >
              {archive.pending ? 'Archiving…' : 'Archive'}
            </button>
          )}
        </div>
        <h2
          className="entity-name"
          style={{ fontWeight: 300, fontSize: 28, margin: '6px 0 0', letterSpacing: '-0.01em', lineHeight: 1.25 }}
        >
          {subject}
          {status === 'archived' && (
            <span className="eyebrow mute" style={{ marginLeft: 12, fontStyle: 'normal', verticalAlign: 'middle' }}>
              Archived
            </span>
          )}
        </h2>

        {participants.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '15px 0 12px' }}>
            {participants.map((p) => {
              const you = !!currentUserId && p.userId === currentUserId;
              const read = hasReadThrough(p, lastMessageAt);
              return (
                <span key={p.userId} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'var(--bg)', border: '1px solid var(--hairline)', borderRadius: 999, padding: '4px 12px 4px 4px', fontSize: 12.5, color: 'var(--ink-2)' }}>
                  <Avatar initials={initialsOf(displayNameOf(p.displayName))} color={you ? 'teal-soft' : ''} size={24} />
                  <span style={{ color: 'var(--ink)' }}>
                    {displayNameOf(p.displayName)}
                    {you && <span style={{ color: 'var(--primary-600)', fontWeight: 600 }}> · You</span>}
                  </span>
                  <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>· {participantRoleLabel(p.role)}</span>
                  <span
                    aria-hidden="true"
                    title={p.lastReadAt ? `Read ${fmtListTime(p.lastReadAt)}` : 'Hasn’t opened this yet'}
                    style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: read ? 'var(--positive)' : 'var(--ink-4)' }}
                  />
                </span>
              );
            })}
            {/* Lane B - B7: admin-tier add-participant, at the end of the roster. */}
            {addParticipant && (
              <AddParticipantControl
                candidates={addParticipant.candidates}
                onAdd={addParticipant.onAdd}
                pending={addParticipant.pending}
              />
            )}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--positive)', background: 'var(--positive-soft)', padding: '3px 10px', borderRadius: 999, fontWeight: 500 }}>
            <Icon name="shield" size={12} /> Audit-logged
          </span>
          {participants.length > 0 && (
            <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
              Visible to {participants.length} {participants.length === 1 ? 'person' : 'people'} on this thread
            </span>
          )}
        </div>
      </div>

      {/* Messages — date separators + grouped consecutive authors + attachments */}
      <div style={{ padding: '22px 32px', display: 'flex', flexDirection: 'column', gap: 18, flex: 1 }}>
        {messages.length === 0 && (
          <div style={{ color: 'var(--ink-3)', fontSize: 14 }}>
            No messages in this thread yet. Send the first reply below to start the conversation.
          </div>
        )}
        {messages.map((m, idx) => {
          const prev = idx > 0 ? messages[idx - 1] : undefined;
          const newDay = !prev || dayLabel(prev.createdAt) !== dayLabel(m.createdAt);
          const grouped = !newDay && !!prev && prev.authorUserId === m.authorUserId;
          const you = !!currentUserId && m.authorUserId === currentUserId;
          return (
            <div key={m.id}>
              {newDay && (
                <div className="eyebrow mute" style={{ display: 'flex', alignItems: 'center', gap: 14, margin: idx === 0 ? '0 0 18px' : '6px 0 18px' }}>
                  <span style={{ height: 1, background: 'var(--hairline)', flex: 1 }} />
                  {dayLabel(m.createdAt)}
                  <span style={{ height: 1, background: 'var(--hairline)', flex: 1 }} />
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr', gap: 14 }}>
                {grouped ? <span /> : <Avatar initials={initialsOf(displayNameOf(m.authorDisplayName))} color={you ? 'teal-soft' : ''} size={36} />}
                <div style={{ minWidth: 0 }}>
                  {!grouped && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>
                        {displayNameOf(m.authorDisplayName)}
                        {you && <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.01em', color: 'var(--primary-600)', marginLeft: 7 }}>You</span>}
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 11.5, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>
                          {fmtListTime(m.createdAt)}
                        </span>
                        {canRedact && !m.redactedAt && (
                          <button
                            type="button"
                            onClick={() => onRequestRedact(m)}
                            title="Redact this message"
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                              fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--sans)',
                              textDecoration: 'underline', textUnderlineOffset: 2,
                            }}
                          >
                            Redact
                          </button>
                        )}
                      </span>
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: 14.5, lineHeight: 1.6, marginTop: grouped ? 0 : 4, whiteSpace: 'pre-wrap',
                      color: m.redactedAt ? 'var(--ink-4)' : 'var(--ink-2)',
                      fontStyle: m.redactedAt ? 'italic' : 'normal',
                    }}
                  >
                    {m.redactedAt ? '[redacted]' : m.body}
                  </div>
                  {!m.redactedAt && m.attachments.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 9 }}>
                      {m.attachments.map((a, ai) => (
                        <span key={ai} title={a.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--primary-700)', background: 'var(--primary-50)', border: '1px solid var(--primary-100)', padding: '6px 11px', borderRadius: 9, maxWidth: '100%' }}>
                          <Icon name="doc" size={13} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.label}</span>
                          {a.sizeBytes > 0 && <span style={{ color: 'var(--ink-3)', flexShrink: 0 }}>· {formatBytes(a.sizeBytes)}</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {messages.length > 0 && everyoneRead && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'flex-end', fontSize: 11.5, color: 'var(--ink-3)' }}>
            <Icon name="check" size={13} /> Read by all participants
          </div>
        )}
      </div>

      {/* Composer */}
      <div style={{ padding: '16px 32px 24px', borderTop: '1px solid var(--hairline)' }}>
        <label htmlFor="inbox-composer" style={{ display: 'none' }}>
          Reply
        </label>
        <textarea
          id="inbox-composer"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          placeholder="Write a reply. Messages are recorded in the trust audit log."
          rows={3}
          style={{
            width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid var(--hairline-2)',
            background: 'var(--bg)', color: 'var(--ink)', fontFamily: 'var(--sans)', fontSize: 14,
            lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box',
          }}
          disabled={isSending}
        />
        {sendError && (
          <div role="alert" style={{ color: 'var(--ink-2)', fontSize: 12.5, marginTop: 6 }}>
            Your draft is still here. {sendError}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--ink-3)', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Icon name="shield" size={13} /> Every reply is recorded against this trust.
          </span>
          <button
            type="button"
            onClick={onSend}
            disabled={!canSend}
            style={{
              background: canSend ? 'var(--primary-700)' : 'var(--surface-2)',
              color: canSend ? 'var(--surface)' : 'var(--ink-3)',
              border: 'none', padding: '11px 20px', borderRadius: 10, fontFamily: 'var(--sans)',
              fontSize: 14, fontWeight: 500, cursor: canSend ? 'pointer' : 'not-allowed',
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
          >
            {isSending ? 'Sending…' : 'Send reply'}
          </button>
        </div>
      </div>
    </div>
  );
}
